import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  Plus,
  Shield,
  Lock,
  Check,
  X,
  Trash2,
  Settings,
  Sparkles,
  CheckCircle2,
  ArrowLeft,
  RotateCcw,
  Star,
  Pencil,
  Eye,
  EyeOff,
  Sliders,
  Tv,
  Film,
  Tv2,
  Baby,
  KeyRound,
  LogOut,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { UserProfile } from '../../types/iptv';
import { OpenTvLoader } from '../common/OpenTvLoader';
import { APP_VERSION } from '../../version';
import { applyProfileTheme } from '../../utils/themeColors';
import { hashPin, isPinValid } from '../../utils/security';

// Avatares ricos: bonecos, personagens, animais, cinema e ícones modernos
const AVATAR_CATEGORIES = [
  {
    category: 'Personagens & Bonecos',
    avatars: ['👑', '🧙‍♂️', '🦸‍♂️', '🦹‍♀️', '🤖', '👾', '🤠', '🥷', '🧑‍🚀', '👸', '🧔', '👩‍🦰'],
  },
  {
    category: 'Cinema & Entretenimento',
    avatars: ['🎬', '🍿', '📺', '🎧', '🎸', '🎮', '🏎️', '⚽', '🏆', '🚀', '⚡', '🔥'],
  },
  {
    category: 'Infantil & Animais',
    avatars: ['🦁', '🐱', '🐼', '🦄', '🐶', '🦊', '🐨', '🐯', '🐸', '🦖', '🐬', '🧸'],
  },
];

const COLOR_OPTIONS = [
  { name: 'Ciano Neon', hex: '#06b6d4' },
  { name: 'Âmbar Dourado', hex: '#f59e0b' },
  { name: 'Rosa Magenta', hex: '#ec4899' },
  { name: 'Verde Esmeralda', hex: '#10b981' },
  { name: 'Azul Real', hex: '#3b82f6' },
  { name: 'Roxo Elétrico', hex: '#8b5cf6' },
  { name: 'Vermelho Fogo', hex: '#ef4444' },
  { name: 'Laranja Solar', hex: '#f97316' },
  { name: 'Índigo Noturno', hex: '#6366f1' },
  { name: 'Teal Moderno', hex: '#14b8a6' },
];

const RATING_OPTIONS: { id: 'L' | '10' | '12' | '14' | '16' | '18'; label: string; desc: string; color: string }[] = [
  { id: 'L', label: 'L', desc: 'Livre para todos os públicos', color: 'bg-emerald-500 text-black font-black' },
  { id: '10', label: '10+', desc: 'Recomendado para maiores de 10 anos', color: 'bg-blue-500 text-white font-black' },
  { id: '12', label: '12+', desc: 'Recomendado para maiores de 12 anos', color: 'bg-yellow-500 text-black font-black' },
  { id: '14', label: '14+', desc: 'Recomendado para maiores de 14 anos', color: 'bg-orange-500 text-white font-black' },
  { id: '16', label: '16+', desc: 'Recomendado para maiores de 16 anos', color: 'bg-red-600 text-white font-black' },
  { id: '18', label: '18+', desc: 'Sem restrições (Inclui conteúdo adulto)', color: 'bg-black text-red-500 border border-red-500/60 font-black' },
];

interface ProfileSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPinProfile?: UserProfile | null;
  startInManageMode?: boolean;
}

export const ProfileSelectorModal: React.FC<ProfileSelectorModalProps> = ({
  isOpen,
  onClose,
  initialPinProfile,
  startInManageMode = false,
}) => {
  const {
    profiles,
    currentProfile,
    selectProfile,
    createProfile,
    updateProfile,
    deleteProfile,
    parentalConfig,
    setParentalConfig,
    channelCategories,
    movieCategories,
    seriesCategories,
    logoutUser,
    setIsAuthModalOpen,
    showToast,
    isProfileKicked,
    authUser,
  } = useIPTV();

  // V8.4.5: SÓ o perfil Master cria, edita e exclui perfis — o PIN do Master NÃO libera outro perfil.
  const isMasterProfile = (p?: UserProfile | null): boolean => {
    if (!p) return false;
    const anyFlagged = profiles.some((x) => !!x.isMaster);
    return anyFlagged ? !!p.isMaster : (p.id === 'prof-1' || p.id === profiles[0]?.id);
  };
  const canManageProfiles = (): boolean => isMasterProfile(currentProfile);
  const denyManage = () => showToast('Somente o perfil Master pode criar, editar ou excluir perfis.', 'warning');

  const maxAllowed = authUser?.max_profiles || 5;
  const [showLimitAlert, setShowLimitAlert] = useState(false);

  // Mode states: 'who_is_watching' | 'manage_grid' | 'edit_profile' | 'create_profile'
  const [viewState, setViewState] = useState<'who_is_watching' | 'manage_grid' | 'edit_profile' | 'create_profile'>('who_is_watching');
  const [editingProfile, setEditingProfile] = useState<UserProfile | null>(null);
  const [isSelectingProfile, setIsSelectingProfile] = useState(false);
  const [animatingProfileId, setAnimatingProfileId] = useState<string | null>(null);

  // PIN Prompts
  const [pinPromptTarget, setPinPromptTarget] = useState<UserProfile | null>(null);
  const [masterPinRequiredForManage, setMasterPinRequiredForManage] = useState(false);
  const [masterPinRequiredForLogout, setMasterPinRequiredForLogout] = useState(false);
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinAttempts, setPinAttempts] = useState(0);
  const [pinLockoutSeconds, setPinLockoutSeconds] = useState(0);

  // Contador de bloqueio temporário por excesso de tentativas incorretas
  useEffect(() => {
    if (pinLockoutSeconds <= 0) return;
    const interval = setInterval(() => {
      setPinLockoutSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [pinLockoutSeconds]);

  const handleFailedPinAttempt = () => {
    const nextAttempts = pinAttempts + 1;
    setPinError(true);
    setEnteredPin('');
    if (nextAttempts >= 5) {
      setPinLockoutSeconds(60);
      setPinAttempts(0);
      showToast('Limite de 5 tentativas atingido! Entrada bloqueada por 60s.', 'error');
    } else {
      setPinAttempts(nextAttempts);
      showToast(`PIN incorreto! (${nextAttempts}/5 tentativas)`, 'error');
    }
  };

  // Form states
  const [formName, setFormName] = useState('');
  const [formAvatar, setFormAvatar] = useState('👑');
  const [formColor, setFormColor] = useState('#f59e0b');
  const [formIsKids, setFormIsKids] = useState(false);
  const [formHasPin, setFormHasPin] = useState(false);
  const [formPin, setFormPin] = useState('');
  const [formMaxRating, setFormMaxRating] = useState<'L' | '10' | '12' | '14' | '16' | '18'>('18');
  const [formBlockedCategories, setFormBlockedCategories] = useState<string[]>([]);
  const [categoryFilterTab, setCategoryFilterTab] = useState<'channels' | 'movies' | 'series'>('channels');
  const [isBrowsingCategoriesInside, setIsBrowsingCategoriesInside] = useState(false);

  // Reset ONLY when modal transitions from closed to open
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      prevOpenRef.current = true;
      setIsBrowsingCategoriesInside(false);
      if (initialPinProfile) {
        const hasPin = !!(initialPinProfile.hasPin && initialPinProfile.pin && initialPinProfile.pin.trim().length > 0);
        if (hasPin) {
          setPinPromptTarget(initialPinProfile);
        } else {
          selectProfile(initialPinProfile);
          onClose();
        }
        setViewState('who_is_watching');
      } else if (startInManageMode) {
        setMasterPinRequiredForManage(false);
        if (canManageProfiles()) {
          setViewState('manage_grid');
        } else {
          denyManage();
          setViewState('who_is_watching');
        }
      } else {
        setViewState('who_is_watching');
        setPinPromptTarget(null);
        setMasterPinRequiredForManage(false);
      }
      setMasterPinRequiredForLogout(false);
      setEnteredPin('');
      setPinError(false);
    } else if (!isOpen) {
      prevOpenRef.current = false;
      setIsBrowsingCategoriesInside(false);
      setMasterPinRequiredForLogout(false);
      setPinPromptTarget(null);
      setMasterPinRequiredForManage(false);
      setEnteredPin('');
      setPinError(false);
    }
  }, [isOpen, initialPinProfile, startInManageMode, currentProfile, profiles, selectProfile, onClose]);

  // Enquanto a tela de seleção de perfis ("Quem está assistindo?") estiver aberta,
  // força a cor do sistema em tons de amarelo (#f59e0b).
  // A cor personalizada do perfil só assume quando entra dentro do perfil.
  useEffect(() => {
    if (isOpen) {
      applyProfileTheme('#f59e0b');
    }
  }, [isOpen]);

  const handleBackToApp = () => {
    if (currentProfile?.color) {
      applyProfileTheme(currentProfile.color);
    }
    onClose();
  };

  // Handle ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Back' || e.key === 'BrowserBack') {
        if (isBrowsingCategoriesInside) {
          e.preventDefault();
          e.stopPropagation();
          setIsBrowsingCategoriesInside(false);
          document.getElementById('category-blocklist-container')?.focus();
          return;
        }

        if (pinPromptTarget || masterPinRequiredForManage || masterPinRequiredForLogout) {
          setPinPromptTarget(null);
          setMasterPinRequiredForManage(false);
          setMasterPinRequiredForLogout(false);
          setEnteredPin('');
          setPinError(false);
        } else if (viewState === 'edit_profile' || viewState === 'create_profile') {
          setViewState('manage_grid');
        } else if (viewState === 'manage_grid') {
          setViewState('who_is_watching');
        } else {
          handleBackToApp();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, viewState, isBrowsingCategoriesInside, pinPromptTarget, masterPinRequiredForManage, masterPinRequiredForLogout, onClose, currentProfile]);

  if (!isOpen) return null;

  // Handlers for selection & PIN
  const handleSelectCard = async (prof: UserProfile) => {
    if (isSelectingProfile || animatingProfileId) return;
    const hasActivePin = !!(prof.hasPin && prof.pin && prof.pin.trim().length > 0);
    if (hasActivePin) {
      setPinPromptTarget(prof);
      setEnteredPin('');
      setPinError(false);
      return;
    }
    setAnimatingProfileId(prof.id);
    setIsSelectingProfile(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 260));
      const success = await selectProfile(prof);
      if (success) {
        if (prof.color) {
          applyProfileTheme(prof.color);
        }
        onClose();
      }
    } finally {
      setIsSelectingProfile(false);
      setAnimatingProfileId(null);
    }
  };

  const handleVerifyProfilePin = async (e?: React.FormEvent | string) => {
    if (typeof e !== 'string' && e) e.preventDefault();
    if (!pinPromptTarget || isSelectingProfile) return;
    if (pinLockoutSeconds > 0) {
      showToast(`PIN bloqueado temporariamente. Aguarde ${pinLockoutSeconds}s.`, 'warning');
      return;
    }

    const pinToCheck = typeof e === 'string' ? e : enteredPin;
    const expectedPin = pinPromptTarget.pin?.trim();
    const masterProfile = profiles.find((p) => p.isMaster || p.id === 'prof-1') || profiles[0];
    const expectedMasterPin = masterProfile?.pin && masterProfile.pin.trim().length > 0 ? masterProfile.pin.trim() : parentalConfig.masterPin?.trim();

    const isDirectMatch = !expectedPin || await isPinValid(pinToCheck, expectedPin);
    const isMasterMatch = expectedMasterPin ? await isPinValid(pinToCheck, expectedMasterPin) : false;

    if (isDirectMatch || isMasterMatch) {
      setIsSelectingProfile(true);
      try {
        const success = await selectProfile(pinPromptTarget, pinToCheck);
        if (success) {
          if (pinPromptTarget.color) {
            applyProfileTheme(pinPromptTarget.color);
          }
          setPinAttempts(0);
          setPinPromptTarget(null);
          onClose();
        }
      } finally {
        setIsSelectingProfile(false);
      }
    } else {
      handleFailedPinAttempt();
    }
  };

  const handleVerifyMasterPinForManage = async (e?: React.FormEvent | string) => {
    if (typeof e !== 'string' && e) e.preventDefault();
    // V8.4.5: o PIN do Master não libera o gerenciamento para outro perfil
    if (!canManageProfiles()) {
      setMasterPinRequiredForManage(false);
      setEnteredPin('');
      denyManage();
      return;
    }
    if (pinLockoutSeconds > 0) {
      showToast(`PIN bloqueado temporariamente. Aguarde ${pinLockoutSeconds}s.`, 'warning');
      return;
    }
    const pinToCheck = typeof e === 'string' ? e : enteredPin;
    const masterProfile = profiles.find((p) => p.isMaster || p.id === 'prof-1') || profiles[0];
    const expectedMasterPin = masterProfile?.pin && masterProfile.pin.trim().length > 0 ? masterProfile.pin.trim() : parentalConfig.masterPin?.trim();

    const isMasterMatch = expectedMasterPin ? await isPinValid(pinToCheck, expectedMasterPin) : false;

    if (isMasterMatch) {
      setPinAttempts(0);
      setMasterPinRequiredForManage(false);
      setViewState('manage_grid');
      setEnteredPin('');
      setPinError(false);
      showToast('Acesso de Gerenciamento Autorizado', 'success');
    } else {
      handleFailedPinAttempt();
    }
  };

  const handleVerifyMasterPinForLogout = async (e?: React.FormEvent | string) => {
    if (typeof e !== 'string' && e) e.preventDefault();
    if (pinLockoutSeconds > 0) {
      showToast(`PIN bloqueado temporariamente. Aguarde ${pinLockoutSeconds}s.`, 'warning');
      return;
    }
    const pinToCheck = typeof e === 'string' ? e : enteredPin;
    const masterProfile = profiles.find((p) => p.isMaster || p.id === 'prof-1') || profiles[0];
    const expectedMasterPin = masterProfile?.pin && masterProfile.pin.trim().length > 0 ? masterProfile.pin.trim() : parentalConfig.masterPin?.trim();

    const isMasterMatch = expectedMasterPin ? await isPinValid(pinToCheck, expectedMasterPin) : false;

    if (isMasterMatch) {
      setPinAttempts(0);
      setMasterPinRequiredForLogout(false);
      logoutUser();
      onClose();
      setIsAuthModalOpen(true);
      showToast('Desconectado com sucesso.', 'info');
    } else {
      handleFailedPinAttempt();
    }
  };

  const handleStartLogout = () => {
    const masterProfile = profiles.find((p) => p.isMaster || p.id === 'prof-1') || profiles[0];
    const hasMasterPin = !!(masterProfile?.hasPin && masterProfile?.pin && masterProfile.pin.trim().length > 0);

    if (hasMasterPin) {
      setMasterPinRequiredForLogout(true);
      setEnteredPin('');
      setPinError(false);
    } else {
      if (window.confirm('Deseja realmente desconectar sua conta do dispositivo?')) {
        logoutUser();
        onClose();
        setIsAuthModalOpen(true);
        showToast('Desconectado com sucesso.', 'info');
      }
    }
  };

  const handleStartManageGrid = () => {
    setMasterPinRequiredForManage(false);
    if (!canManageProfiles()) {
      denyManage();
      return;
    }
    setViewState('manage_grid');
  };

  const handleOpenEdit = (prof: UserProfile) => {
    if (!canManageProfiles()) { denyManage(); return; }
    setEditingProfile(prof);
    setFormName(prof.name);
    setFormAvatar(prof.avatar || '👤');
    setFormColor(prof.color || '#f59e0b');
    setFormIsKids(!!prof.isKids);
    const validPin = prof.hasPin && prof.pin && prof.pin.trim().length === 4 ? prof.pin.trim() : '';
    setFormHasPin(!!validPin);
    setFormPin(validPin);
    setFormMaxRating(prof.parentalSettings?.maxRating || (prof.isKids ? 'L' : '18'));
    setFormBlockedCategories(prof.parentalSettings?.blockedCategories || []);
    setViewState('edit_profile');
  };

  const handleOpenCreate = () => {
    if (!canManageProfiles()) { denyManage(); return; }
    if (profiles.length >= maxAllowed) {
      setShowLimitAlert(true);
      return;
    }
    const randomAvatar = AVATAR_CATEGORIES[0].avatars[Math.floor(Math.random() * AVATAR_CATEGORIES[0].avatars.length)];
    const randomColor = COLOR_OPTIONS[profiles.length % COLOR_OPTIONS.length].hex;
    setEditingProfile(null);
    setFormName('');
    setFormAvatar(randomAvatar);
    setFormColor(randomColor);
    setFormIsKids(false);
    setFormHasPin(false);
    setFormPin('');
    setFormMaxRating('18');
    setFormBlockedCategories([]);
    setViewState('create_profile');
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageProfiles()) { denyManage(); return; }
    if (!formName.trim()) {
      showToast('Digite um nome para o perfil', 'warning');
      return;
    }

    const parentalSettings = {
      maxRating: formIsKids ? (formMaxRating === '18' ? 'L' : formMaxRating) : formMaxRating,
      blockedCategories: formBlockedCategories,
    };

    const isMaster = editingProfile ? (editingProfile.isMaster || editingProfile.id === 'prof-1' || editingProfile.id === profiles[0]?.id) : false;
    const hasPin = formHasPin && formPin.trim().length === 4;
    const pinVal = hasPin ? await hashPin(formPin.trim()) : '';

    if (viewState === 'create_profile') {
      await createProfile({
        name: formName.trim(),
        avatar: formAvatar,
        color: formColor,
        isKids: formIsKids,
        hasPin,
        pin: pinVal,
        parentalSettings,
      });
    } else if (viewState === 'edit_profile' && editingProfile) {
      await updateProfile(editingProfile.id, {
        name: formName.trim(),
        avatar: formAvatar,
        color: formColor,
        isKids: formIsKids,
        hasPin,
        pin: pinVal,
        parentalSettings,
      });

      if (isMaster) {
        setParentalConfig((prev) => ({
          ...prev,
          masterPin: pinVal || '',
        }));
      }
    }

    setViewState('manage_grid');
  };

  const handleDelete = async (id: string) => {
    if (!canManageProfiles()) { denyManage(); return; }
    if (isMasterProfile(profiles.find((x) => x.id === id) || null)) {
      showToast('O perfil Master não pode ser excluído.', 'warning');
      return;
    }
    if (profiles.length <= 1) {
      showToast('Não é possível excluir o único perfil existente.', 'warning');
      return;
    }
    if (window.confirm('Tem certeza que deseja excluir este perfil e seu histórico?')) {
      await deleteProfile(id);
      setViewState('manage_grid');
    }
  };

  const toggleCategoryBlock = (catName: string) => {
    setFormBlockedCategories((prev) =>
      prev.includes(catName) ? prev.filter((c) => c !== catName) : [...prev, catName]
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] w-screen h-screen bg-black flex items-center justify-center p-4 sm:p-6 overflow-y-auto animate-in fade-in duration-200 select-none"
    >
      {/* OVERLAY DE CARREGAMENTO: TV DO LOGO COM A TELA PISCANDO / CARREGANDO */}
      {isSelectingProfile && (
        <div className="fixed inset-0 z-[10000] w-screen h-screen bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-6 select-none animate-in fade-in duration-200">
          <OpenTvLoader size="hero" glow="full" />
        </div>
      )}
      {/* PIN Prompt Modal for Profile */}
      {pinPromptTarget && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-150 text-center relative">
          <button
            onClick={() => setPinPromptTarget(null)}
            className="absolute top-4 right-4 p-2 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
          >
            <X className="w-5 h-5" />
          </button>

          <div
            className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center text-4xl mb-4 shadow-xl border-2"
            style={{
              backgroundColor: `${pinPromptTarget.color || '#f59e0b'}25`,
              borderColor: pinPromptTarget.color || '#f59e0b',
            }}
          >
            {pinPromptTarget.avatar}
          </div>

          <h3 className="text-xl font-black text-white mb-1">Perfil Protegido</h3>
          <p className="text-xs text-zinc-400 mb-6">
            Digite o PIN de 4 dígitos para acessar <strong>{pinPromptTarget.name}</strong>
          </p>

          <div className="space-y-4">
            {pinLockoutSeconds > 0 && (
              <div className="text-xs text-red-400 font-bold bg-red-500/15 border border-red-500/30 rounded-xl p-2.5 flex items-center justify-center gap-1.5 animate-pulse">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Bloqueado temporariamente. Aguarde {pinLockoutSeconds}s</span>
              </div>
            )}
            <input
              type="tel"
              inputMode="numeric"
              maxLength={4}
              disabled={pinLockoutSeconds > 0}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              name="open_tv_profile_pin"
              value={enteredPin}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && enteredPin.length === 4) {
                  handleVerifyProfilePin();
                }
              }}
              onChange={(e) => {
                setPinError(false);
                const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                setEnteredPin(val);
                if (val.length === 4) {
                  handleVerifyProfilePin(val);
                }
              }}
              placeholder="0000"
              className={`w-full py-3.5 text-center text-3xl font-mono tracking-widest rounded-2xl bg-zinc-950 border ${
                pinError ? 'border-red-500 text-red-400 animate-shake' : 'border-zinc-800 text-cyan-300 font-black focus:border-theme-accent'
              } focus:outline-none transition disabled:opacity-40`}
            />

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPinPromptTarget(null)}
                className="py-3 rounded-2xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleVerifyProfilePin()}
                disabled={enteredPin.length < 4 || pinLockoutSeconds > 0}
                className="py-3 rounded-2xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-black font-black text-xs transition cursor-pointer shadow-lg shadow-cyan-500/30"
              >
                Entrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Master PIN Prompt for Management Access */}
      {masterPinRequiredForManage && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-150 text-center relative">
          <button
            onClick={() => setMasterPinRequiredForManage(false)}
            className="absolute top-4 right-4 p-2 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 mx-auto flex items-center justify-center text-2xl mb-4 shadow-xl">
            👑
          </div>

          <h3 className="text-xl font-black text-white mb-1">Acesso Master</h3>
          <p className="text-xs text-zinc-400 mb-6">
            Digite o PIN do perfil Master para gerenciar os perfis desta conta
          </p>

          <div className="space-y-4">
            {pinLockoutSeconds > 0 && (
              <div className="text-xs text-red-400 font-bold bg-red-500/15 border border-red-500/30 rounded-xl p-2.5 flex items-center justify-center gap-1.5 animate-pulse">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Bloqueado temporariamente. Aguarde {pinLockoutSeconds}s</span>
              </div>
            )}
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              disabled={pinLockoutSeconds > 0}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              name="open_tv_master_pin"
              value={enteredPin}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && enteredPin.length === 4) {
                  handleVerifyMasterPinForManage();
                }
              }}
              onChange={(e) => {
                setPinError(false);
                const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                setEnteredPin(val);
                if (val.length === 4) {
                  handleVerifyMasterPinForManage(val);
                }
              }}
              placeholder="0000"
              className={`w-full py-3.5 text-center text-3xl font-mono tracking-widest rounded-2xl bg-zinc-950 border ${
                pinError ? 'border-red-500 text-red-400 animate-shake' : 'border-zinc-800 text-amber-300 font-black focus:border-amber-400'
              } focus:outline-none transition disabled:opacity-40`}
            />

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => setMasterPinRequiredForManage(false)}
                className="py-3 rounded-2xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleVerifyMasterPinForManage()}
                disabled={enteredPin.length < 4 || pinLockoutSeconds > 0}
                className="py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-black text-xs transition cursor-pointer shadow-lg shadow-amber-500/30"
              >
                Acessar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Master PIN Prompt for Logout (Sair da Conta - Apenas Usuário Master) */}
      {masterPinRequiredForLogout && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-150 text-center relative">
          <button
            onClick={() => setMasterPinRequiredForLogout(false)}
            className="absolute top-4 right-4 p-2 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-400 mx-auto flex items-center justify-center text-2xl mb-4 shadow-xl">
            <LogOut className="w-8 h-8" />
          </div>

          <h3 className="text-xl font-black text-white mb-1">Encerrar Sessão</h3>
          <p className="text-xs text-zinc-400 mb-6">
            Digite o PIN do perfil Master desta conta para desconectar o aparelho
          </p>

          <div className="space-y-4">
            {pinLockoutSeconds > 0 && (
              <div className="text-xs text-red-400 font-bold bg-red-500/15 border border-red-500/30 rounded-xl p-2.5 flex items-center justify-center gap-1.5 animate-pulse">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Bloqueado temporariamente. Aguarde {pinLockoutSeconds}s</span>
              </div>
            )}
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              disabled={pinLockoutSeconds > 0}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              name="open_tv_logout_pin"
              value={enteredPin}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && enteredPin.length === 4) {
                  handleVerifyMasterPinForLogout();
                }
              }}
              onChange={(e) => {
                setPinError(false);
                const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                setEnteredPin(val);
                if (val.length === 4) {
                  handleVerifyMasterPinForLogout(val);
                }
              }}
              placeholder="0000"
              className={`w-full py-3.5 text-center text-3xl font-mono tracking-widest rounded-2xl bg-zinc-950 border ${
                pinError ? 'border-red-500 text-red-400 animate-shake' : 'border-zinc-800 text-white focus:border-red-400'
              } focus:outline-none transition disabled:opacity-40`}
            />

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => setMasterPinRequiredForLogout(false)}
                className="py-3 rounded-2xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleVerifyMasterPinForLogout()}
                disabled={enteredPin.length < 4 || pinLockoutSeconds > 0}
                className="py-3 rounded-2xl bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-black text-xs transition cursor-pointer shadow-lg shadow-red-600/30"
              >
                Desconectar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      {!pinPromptTarget && !masterPinRequiredForManage && !masterPinRequiredForLogout && (
        <div className="w-full max-w-4xl flex flex-col items-center justify-center my-auto py-6">
          {/* SCREEN 1 & 2: WHO IS WATCHING OR MANAGE GRID */}
          {(viewState === 'who_is_watching' || viewState === 'manage_grid') && (
            <div className="flex flex-col items-center text-center w-full animate-in fade-in duration-200">
              {/* OPEN TV Logo - Apenas a TV ampliada e texto limpo sem caixas/quadrados */}
              <div className="flex items-center justify-center gap-2.5 mb-6 sm:mb-8">
                <svg
                  viewBox="65 60 382 360"
                  className="w-14 h-14 sm:w-16 sm:h-16 drop-shadow-[0_0_25px_rgba(245,158,11,0.6)] shrink-0"
                >
                  {/* Antenas */}
                  <path
                    d="M176 82L256 160L336 82"
                    fill="none"
                    stroke="#F59E0B"
                    strokeWidth="32"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Moldura da TV */}
                  <rect
                    x="86"
                    y="160"
                    width="340"
                    height="246"
                    rx="42"
                    fill="none"
                    stroke="#F59E0B"
                    strokeWidth="32"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Tela interna */}
                  <rect
                    x="114"
                    y="188"
                    width="284"
                    height="190"
                    rx="16"
                    fill="#F59E0B"
                    fillOpacity="0.2"
                  />
                </svg>
                <div className="flex items-center gap-2">
                  <span className="font-black text-2xl sm:text-4xl text-white tracking-wider">OPEN TV</span>
                  <span
                    style={{
                      backgroundColor: 'rgba(245, 158, 11, 0.18)',
                      borderColor: 'rgba(245, 158, 11, 0.45)',
                      color: '#fbbf24',
                    }}
                    className="px-2 py-0.5 rounded-lg border text-[11px] sm:text-xs font-mono font-bold shadow-sm shadow-amber-500/20 select-none"
                  >
                    v{APP_VERSION}
                  </span>
                </div>
              </div>

              <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight mb-4 sm:mb-6">
                {viewState === 'who_is_watching' ? 'Quem está assistindo?' : 'Gerenciar Perfis'}
              </h1>

              {isProfileKicked && viewState === 'who_is_watching' && (
                <div className="mb-6 px-4 py-3 rounded-xl bg-amber-500/20 border border-amber-500/50 flex items-center gap-3 text-amber-300 max-w-md animate-in fade-in zoom-in-95 duration-200">
                  <span className="text-xl">⚠️</span>
                  <div className="text-left text-xs sm:text-sm">
                    <strong className="block text-amber-200 uppercase font-black tracking-wide">Perfil em Uso</strong>
                    Usuário assistindo em outro dispositivo. Escolha outro perfil ou entre novamente para reassumir.
                  </div>
                </div>
              )}

              {viewState === 'manage_grid' && (
                <p className="text-xs sm:text-sm text-zinc-400 mb-8 sm:mb-10 -mt-2">
                  Selecione um perfil para editar avatares, faixas etárias e categorias bloqueadas
                </p>
              )}

              {/* Profiles Grid (Netflix Style - Responsivo para Celular e TV) */}
              <div className="flex flex-wrap items-center justify-center gap-3.5 sm:gap-8 md:gap-10 mb-8 sm:mb-12 px-2">
                {profiles.map((p) => {
                  const isCurrent = currentProfile ? p.id === currentProfile.id : false;
                  const isMaster = p.isMaster || p.id === 'prof-1';
                  const isSelectedForAnimation = animatingProfileId === p.id;
                  const isOtherBeingAnimated = animatingProfileId !== null && !isSelectedForAnimation;
                  return (
                    <div
                      key={p.id}
                      onClick={() => (viewState === 'manage_grid' ? handleOpenEdit(p) : handleSelectCard(p))}
                      className={`group no-default-focus flex flex-col items-center cursor-pointer transition-all duration-300 outline-none focus:outline-none focus:ring-0 select-none ${
                        isOtherBeingAnimated ? 'opacity-25 scale-95 pointer-events-none' : ''
                      }`}
                      data-no-focus-outline="true"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          viewState === 'manage_grid' ? handleOpenEdit(p) : handleSelectCard(p);
                        }
                      }}
                    >
                      {/* Avatar Card (Foco dinâmico e animação Netflix de zoom-in) */}
                      <div
                        className={`w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-2xl sm:rounded-3xl border-2 bg-zinc-900/60 flex items-center justify-center text-3xl sm:text-5xl md:text-6xl transition-all duration-300 relative shadow-xl ${
                          isSelectedForAnimation
                            ? 'scale-115 sm:scale-120 border-white ring-4 ring-white ring-offset-4 ring-offset-black z-30 shadow-2xl'
                            : 'border-zinc-800 group-hover:scale-105 group-focus:scale-105 group-hover:border-white group-focus:border-white group-hover:ring-4 group-focus:ring-4 group-hover:ring-white group-focus:ring-white group-hover:ring-offset-4 group-focus:ring-offset-4 group-hover:ring-offset-black group-focus:ring-offset-black group-hover:bg-zinc-800 group-focus:bg-zinc-800'
                        }`}
                        style={{
                          backgroundColor: `${p.color || '#3b82f6'}15`,
                        }}
                      >
                        <span>{p.avatar}</span>

                        {/* Master Crown Badge */}
                        {isMaster && (
                          <div
                            style={{
                              backgroundColor: '#f59e0b',
                              borderColor: '#09090b',
                              boxShadow: '0 0 10px rgba(245, 158, 11, 0.6)',
                              color: '#000000',
                            }}
                            className="absolute -top-2 -right-2 sm:-top-2.5 sm:-right-2.5 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs sm:text-sm font-black shadow-md border-2 select-none"
                            title="Usuário Master da Conta"
                          >
                            👑
                          </div>
                        )}

                        {/* Kids Badge */}
                        {p.isKids && (
                          <div className="absolute -bottom-2 sm:-bottom-2.5 px-2 sm:px-2.5 py-0.5 rounded-full bg-emerald-500 text-black text-[9px] sm:text-[10px] font-black uppercase tracking-wider shadow-md">
                            Kids
                          </div>
                        )}

                        {/* Lock Badge */}
                        {p.hasPin && p.pin && p.pin.trim().length > 0 && !p.isKids && (
                          <div className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-zinc-900/90 text-zinc-300 flex items-center justify-center text-[10px] sm:text-xs border border-zinc-700">
                            <Lock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          </div>
                        )}

                        {/* Active Profile Indicator Dot */}
                        {isCurrent && viewState === 'who_is_watching' && (
                          <div
                            style={{
                              backgroundColor: '#f59e0b',
                              boxShadow: '0 0 8px rgba(245, 158, 11, 0.8)',
                            }}
                            className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full select-none"
                            title="Perfil Atual"
                          />
                        )}

                        {/* Edit Pencil Overlay in Manage Mode */}
                        {viewState === 'manage_grid' && (
                          <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-black/60 backdrop-blur-xs flex items-center justify-center text-white opacity-90 group-hover:opacity-100 group-hover:bg-black/40 transition">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 border border-white/40 flex items-center justify-center">
                              <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Profile Name */}
                      <span className="mt-2.5 sm:mt-3.5 text-xs sm:text-base font-bold text-zinc-400 group-hover:text-white group-focus:text-white transition max-w-[100px] sm:max-w-[120px] truncate">
                        {p.name}
                      </span>
                    </div>
                  );
                })}

                {/* Add Profile Card (Apenas no modo Gerenciar Perfis pelo Usuário Master) */}
                {viewState === 'manage_grid' && (
                  <div
                    onClick={handleOpenCreate}
                    className="group no-default-focus flex flex-col items-center cursor-pointer transition-all duration-200 outline-none focus:outline-none focus:ring-0 select-none relative"
                    data-no-focus-outline="true"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleOpenCreate()}
                  >
                    <div className={`w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-2xl sm:rounded-3xl border-2 border-dashed ${
                      profiles.length >= maxAllowed
                        ? 'border-amber-500/50 bg-amber-500/5 group-hover:border-amber-400 group-hover:bg-amber-500/10'
                        : 'border-zinc-700 group-hover:border-white group-focus:border-white group-focus:ring-4 group-focus:ring-white group-focus:ring-offset-4 group-focus:ring-offset-black bg-zinc-900/40 group-hover:bg-zinc-850 group-focus:bg-zinc-850'
                    } flex items-center justify-center text-zinc-500 group-hover:text-white group-focus:text-white group-hover:scale-105 group-focus:scale-105 transition-all shadow-lg relative`}>
                      <Plus className="w-8 h-8 sm:w-12 sm:h-12" />
                      {profiles.length >= maxAllowed && (
                        <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-amber-500 text-black text-[9px] font-black uppercase tracking-wider shadow">
                          Limite
                        </span>
                      )}
                    </div>
                    <span className="mt-2.5 sm:mt-3.5 text-xs sm:text-base font-bold text-zinc-400 group-hover:text-white group-focus:text-white transition">
                      {profiles.length >= maxAllowed ? `Limite (${profiles.length}/${maxAllowed})` : 'Adicionar'}
                    </span>
                  </div>
                )}
              </div>

              {/* Bottom Mode Actions */}
              <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                {viewState === 'who_is_watching' ? (
                  <>
                    {currentProfile && (
                      <button
                        onClick={handleBackToApp}
                        className="px-4 py-2 rounded-xl text-zinc-300 bg-zinc-800/80 border border-zinc-700 hover:bg-zinc-700 hover:text-white focus:bg-white focus:text-black focus:outline-none focus:ring-2 focus:ring-white text-xs font-bold tracking-wide transition-all cursor-pointer flex items-center gap-2 shadow-sm"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span>Voltar ao App</span>
                      </button>
                    )}
                    <button
                      onClick={handleStartManageGrid}
                      className="px-4 py-2 rounded-xl text-zinc-400 bg-zinc-900/70 border border-zinc-800 hover:bg-white hover:text-black hover:border-white focus:bg-white focus:text-black focus:border-white focus:outline-none focus:ring-2 focus:ring-white focus:scale-105 text-xs font-bold tracking-wide transition-all cursor-pointer flex items-center gap-2 shadow-sm"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      <span>Gerenciar Perfis</span>
                    </button>
                    <button
                      onClick={handleStartLogout}
                      className="px-4 py-2 rounded-xl text-zinc-400 bg-zinc-900/70 border border-zinc-800 hover:bg-red-600 hover:text-white hover:border-red-600 focus:bg-red-600 focus:text-white focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:scale-105 text-xs font-bold tracking-wide transition-all cursor-pointer flex items-center gap-2 shadow-sm"
                      title="Sair da conta"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      <span>Sair</span>
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setViewState('who_is_watching')}
                      className="px-4 py-2 rounded-xl text-zinc-400 bg-zinc-900/80 border border-zinc-800 hover:bg-zinc-800 hover:text-zinc-200 text-xs font-bold transition cursor-pointer"
                    >
                      Trocar de Perfil
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (currentProfile) {
                          onClose();
                        } else {
                          setViewState('who_is_watching');
                        }
                      }}
                      className="px-6 py-2 rounded-xl bg-white hover:bg-zinc-200 text-black font-black text-xs tracking-wider uppercase transition cursor-pointer shadow-lg active:scale-95"
                    >
                      Concluído
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SCREEN 3: PROFILE & PARENTAL EDITOR (Netflix Style) */}
          {(viewState === 'edit_profile' || viewState === 'create_profile') && (
            <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl animate-in zoom-in-95 duration-150">
              {/* Header */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 sm:pb-6 border-b border-zinc-800 mb-4 sm:mb-6">
                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setViewState('manage_grid')}
                    className="p-2 sm:p-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 focus:bg-amber-500 focus:text-black focus:outline-none focus:ring-2 focus:ring-amber-400 transition cursor-pointer shrink-0"
                    title="Voltar ao Gerenciador de Perfis"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base sm:text-xl font-black text-white truncate">
                      {viewState === 'create_profile' ? 'Criar Novo Perfil' : 'Editar Perfil & Controle Parental'}
                    </h2>
                    <p className="text-[11px] sm:text-xs text-zinc-400 truncate">
                      Personalize avatar, cor e restrições deste perfil
                    </p>
                  </div>
                </div>
                {editingProfile && !editingProfile.isMaster && editingProfile.id !== 'prof-1' && (
                  <button
                    type="button"
                    onClick={() => handleDelete(editingProfile.id)}
                    className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500 hover:text-white focus:bg-red-600 focus:text-white focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-400 transition text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Excluir</span>
                  </button>
                )}
              </div>

              <form onSubmit={handleSaveProfile} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-zinc-800">
                {/* Profile Identity (Avatar & Name) */}
                <div className="flex flex-col sm:flex-row items-center gap-6 p-4 rounded-2xl bg-zinc-950/60 border border-zinc-800/80">
                  <div
                    className="w-20 h-20 rounded-2xl border-2 flex items-center justify-center text-4xl shrink-0 shadow-lg"
                    style={{
                      backgroundColor: `${formColor}25`,
                      borderColor: formColor,
                    }}
                  >
                    {formAvatar}
                  </div>
                  <div className="flex-1 w-full space-y-2">
                    <label className="block text-xs font-bold text-zinc-300">Nome do Perfil</label>
                    <input
                      type="text"
                      required
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="Ex: João, Maria, Quarto..."
                      className="w-full px-4 py-2.5 rounded-xl bg-zinc-900 border border-zinc-700 text-white text-sm font-bold focus:border-theme-accent focus:ring-2 focus:ring-theme-accent focus:outline-none transition"
                    />
                  </div>
                </div>

                {/* Avatar Gallery Picker */}
                <div>
                  <label className="block text-xs font-bold text-zinc-300 mb-2">Escolha o Avatar do Perfil</label>
                  <div className="space-y-3">
                    {AVATAR_CATEGORIES.map((catGroup) => (
                      <div key={catGroup.category} className="space-y-1.5">
                        <div className="text-[11px] font-semibold text-zinc-400 px-1">{catGroup.category}</div>
                        <div className="flex flex-wrap gap-2">
                          {catGroup.avatars.map((av) => (
                            <button
                              key={av}
                              type="button"
                              onClick={() => setFormAvatar(av)}
                              className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 focus:scale-110 ${
                                formAvatar === av
                                  ? 'bg-white/20 border-2 border-white scale-110 shadow-md ring-2 ring-amber-400'
                                  : 'bg-zinc-800/60 border border-zinc-700 hover:bg-zinc-800'
                              }`}
                            >
                              {av}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Color Theme Picker */}
                <div>
                  <label className="block text-xs font-bold text-zinc-300 mb-2">Cor de Destaque do Perfil</label>
                  <div className="flex flex-wrap gap-2.5">
                    {COLOR_OPTIONS.map((col) => (
                      <button
                        key={col.hex}
                        type="button"
                        onClick={() => setFormColor(col.hex)}
                        className={`w-8 h-8 rounded-full transition transform cursor-pointer flex items-center justify-center shadow-md focus:outline-none focus:ring-2 focus:ring-white focus:scale-125 ${
                          formColor === col.hex ? 'scale-125 ring-2 ring-white' : 'hover:scale-110'
                        }`}
                        style={{ backgroundColor: col.hex }}
                        title={col.name}
                      >
                        {formColor === col.hex && <Check className="w-4 h-4 text-black stroke-[3]" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Parental Controls & Age Rating (Netflix Style) */}
                <div className="p-5 rounded-2xl bg-zinc-950/60 border border-zinc-800/80 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <Shield className="w-5 h-5 text-theme-accent" />
                      <div>
                        <div className="text-sm font-bold text-white">Controle Parental &amp; Faixa Etária</div>
                        <div className="text-[11px] text-zinc-400">Classificação indicativa e modo infantil</div>
                      </div>
                    </div>

                    {/* Kids Switch */}
                    <button
                      type="button"
                      onClick={() => {
                        const next = !formIsKids;
                        setFormIsKids(next);
                        if (next) {
                          setFormMaxRating('L');
                          if (!formAvatar || formAvatar === '👑') setFormAvatar('🦁');
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-black transition flex items-center gap-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white focus:scale-105 ${
                        formIsKids
                          ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/30'
                          : 'bg-zinc-800 text-zinc-400 hover:text-white'
                      }`}
                    >
                      <Baby className="w-3.5 h-3.5" />
                      <span>{formIsKids ? 'Espaço Kids Ativo' : 'Ativar Espaço Kids'}</span>
                    </button>
                  </div>

                  {/* Age Rating Badges */}
                  <div>
                    <label className="block text-xs font-semibold text-zinc-300 mb-2">Classificação Máxima Permitida</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {RATING_OPTIONS.map((rate) => (
                        <button
                          key={rate.id}
                          type="button"
                          onClick={() => setFormMaxRating(rate.id)}
                          className={`p-2.5 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 ${
                            formMaxRating === rate.id
                              ? 'bg-zinc-800 border-theme-accent ring-1 ring-theme-accent'
                              : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs ${rate.color}`}>{rate.label}</span>
                            {formMaxRating === rate.id && <CheckCircle2 className="w-4 h-4 text-theme-accent" />}
                          </div>
                          <span className="text-[10px] text-zinc-400 line-clamp-2">{rate.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Specific Categories Blocklist Button */}
                <div className="p-4 sm:p-5 rounded-2xl bg-zinc-950/60 border border-zinc-800/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                      <Sliders className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">Bloqueio de Categorias Específicas</div>
                      <div className="text-[11px] text-zinc-400">
                        {formBlockedCategories.length > 0
                          ? `${formBlockedCategories.length} categorias bloqueadas para este perfil`
                          : 'Nenhuma categoria bloqueada (todas as categorias visíveis)'}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    id="btn-open-category-manager"
                    onClick={() => setIsBrowsingCategoriesInside(true)}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-amber-300 font-bold text-xs border border-zinc-700 hover:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 cursor-pointer flex items-center justify-center gap-2 transition"
                  >
                    <span>Gerenciar Categorias</span>
                    <ChevronRight className="w-4 h-4 text-amber-400" />
                  </button>
                </div>

                {/* Profile Security PIN (Radio Options) */}
                <div className="p-5 rounded-2xl bg-zinc-950/60 border border-zinc-800/80 space-y-4">
                  <div className="flex items-center gap-2.5">
                    <Lock className="w-5 h-5 text-theme-accent" />
                    <div>
                      <div className="text-sm font-bold text-white">Bloqueio de Perfil com PIN</div>
                      <div className="text-[11px] text-zinc-400">
                        Defina se este perfil exigirá senha de 4 dígitos para ser acessado
                      </div>
                    </div>
                  </div>

                  {/* Radio Selector: Sem PIN vs Com PIN */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setFormHasPin(false);
                        setFormPin('');
                      }}
                      className={`p-3.5 rounded-xl border text-left transition flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 ${
                        !formHasPin
                          ? 'bg-zinc-800 border-theme-accent ring-1 ring-theme-accent'
                          : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          !formHasPin ? 'border-theme-accent bg-theme-accent' : 'border-zinc-600'
                        }`}>
                          {!formHasPin && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white">Sem PIN (Livre)</div>
                          <div className="text-[10px] text-zinc-400">Entrada direta com 1 clique</div>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setFormHasPin(true);
                        if (!formPin) setFormPin('1234');
                      }}
                      className={`p-3.5 rounded-xl border text-left transition flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 ${
                        formHasPin
                          ? 'bg-zinc-800 border-theme-accent ring-1 ring-theme-accent'
                          : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          formHasPin ? 'border-theme-accent bg-theme-accent' : 'border-zinc-600'
                        }`}>
                          {formHasPin && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white">Com PIN (Protegido)</div>
                          <div className="text-[10px] text-zinc-400">Exige senha de 4 dígitos</div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {formHasPin && (
                    <div className="pt-2 bg-zinc-900/40 p-4 rounded-xl border border-zinc-800">
                      <label className="block text-xs font-bold text-zinc-300 mb-1.5">
                        Digite o PIN de 4 dígitos do Perfil:
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        name="open_tv_profile_editor_pin"
                        value={formPin}
                        onChange={(e) => setFormPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="0000"
                        className="w-56 py-3 px-4 text-center text-3xl font-mono font-black tracking-widest rounded-2xl bg-zinc-950 border border-theme-accent text-cyan-300 focus:outline-none shadow-lg focus:ring-2 focus:ring-theme-accent"
                      />
                      <p className="text-[10px] text-zinc-400 mt-1">
                        Este PIN será solicitado ao trocar para este perfil e ao acessar o controle parental.
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
                  <button
                    type="button"
                    onClick={() => setViewState('manage_grid')}
                    className="px-6 py-3 rounded-2xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 focus:bg-zinc-700 focus:text-white focus:outline-none focus:ring-2 focus:ring-white text-xs font-bold transition cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-2xl bg-theme-accent hover:opacity-90 text-black font-black text-xs transition cursor-pointer shadow-lg shadow-theme-accent-sm focus:outline-none focus:ring-2 focus:ring-white focus:scale-105"
                  >
                    Salvar Perfil
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* MODAL DEDICADO DE BLOQUEIO DE CATEGORIAS */}
      {isBrowsingCategoriesInside && (() => {
        const currentCats = categoryFilterTab === 'channels'
          ? channelCategories
          : categoryFilterTab === 'movies'
          ? movieCategories
          : seriesCategories;

        return (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 select-none animate-in fade-in duration-150"
          >
            <div className="w-full max-w-xl bg-zinc-900 border border-zinc-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[85vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between pb-3 border-b border-zinc-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center">
                    <Sliders className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white">Bloqueio de Categorias</h3>
                    <p className="text-xs text-zinc-400">Marque as categorias a ocultar para este perfil</p>
                  </div>
                </div>

                {formBlockedCategories.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormBlockedCategories([])}
                    className="text-xs text-amber-400 hover:underline font-bold px-2 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer"
                  >
                    Desbloquear Todas ({formBlockedCategories.length})
                  </button>
                )}
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
                <button
                  type="button"
                  id="cat-modal-tab-channels"
                  onClick={() => setCategoryFilterTab('channels')}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const firstItem = document.getElementById('cat-item-0');
                      firstItem?.focus();
                      firstItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-white cursor-pointer ${
                    categoryFilterTab === 'channels'
                      ? 'bg-theme-accent text-black font-black shadow-md'
                      : 'text-zinc-400 hover:text-white bg-zinc-950'
                  }`}
                >
                  <Tv className="w-3.5 h-3.5" />
                  <span>Ao Vivo</span>
                </button>
                <button
                  type="button"
                  id="cat-modal-tab-movies"
                  onClick={() => setCategoryFilterTab('movies')}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const firstItem = document.getElementById('cat-item-0');
                      firstItem?.focus();
                      firstItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-white cursor-pointer ${
                    categoryFilterTab === 'movies'
                      ? 'bg-theme-accent text-black font-black shadow-md'
                      : 'text-zinc-400 hover:text-white bg-zinc-950'
                  }`}
                >
                  <Film className="w-3.5 h-3.5" />
                  <span>Filmes</span>
                </button>
                <button
                  type="button"
                  id="cat-modal-tab-series"
                  onClick={() => setCategoryFilterTab('series')}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const firstItem = document.getElementById('cat-item-0');
                      firstItem?.focus();
                      firstItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-white cursor-pointer ${
                    categoryFilterTab === 'series'
                      ? 'bg-theme-accent text-black font-black shadow-md'
                      : 'text-zinc-400 hover:text-white bg-zinc-950'
                  }`}
                >
                  <Tv2 className="w-3.5 h-3.5" />
                  <span>Séries</span>
                </button>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-1.5 pr-1 scrollbar-thin scrollbar-thumb-zinc-800">
                {currentCats.map((cat, idx) => {
                  const isBlocked = formBlockedCategories.includes(cat);
                  return (
                    <button
                      key={cat}
                      id={`cat-item-${idx}`}
                      type="button"
                      onClick={() => toggleCategoryBlock(cat)}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          if (idx === 0) {
                            const activeTab = document.getElementById(`cat-modal-tab-${categoryFilterTab}`);
                            activeTab?.focus();
                          } else {
                            const prevItem = document.getElementById(`cat-item-${idx - 1}`);
                            prevItem?.focus();
                            prevItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          }
                        } else if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          if (idx === currentCats.length - 1) {
                            document.getElementById('btn-finish-category-modal')?.focus();
                          } else {
                            const nextItem = document.getElementById(`cat-item-${idx + 1}`);
                            nextItem?.focus();
                            nextItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          }
                        }
                      }}
                      className={`w-full px-4 py-2.5 rounded-xl text-xs font-semibold text-left transition flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 ${
                        isBlocked
                          ? 'bg-red-500/15 border border-red-500/40 text-red-300 font-bold'
                          : 'bg-zinc-950/70 hover:bg-zinc-800 border border-zinc-800/80 text-zinc-300 focus:bg-zinc-800'
                      }`}
                    >
                      <span className="truncate pr-2 font-medium">{cat}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold shrink-0 ${
                        isBlocked ? 'bg-red-500/30 text-red-200 border border-red-500/50' : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        {isBlocked ? '🔒 Bloqueada' : '👁️ Visível'}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="pt-3 border-t border-zinc-800 flex items-center justify-between">
                <span className="text-[11px] text-zinc-500">Pressione ESC no teclado ou Voltar no controle</span>
                <button
                  type="button"
                  id="btn-finish-category-modal"
                  onClick={() => {
                    setIsBrowsingCategoriesInside(false);
                    document.getElementById('btn-open-category-manager')?.focus();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const lastItem = document.getElementById(`cat-item-${currentCats.length - 1}`);
                      lastItem?.focus();
                      lastItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const activeTab = document.getElementById(`cat-modal-tab-${categoryFilterTab}`);
                      activeTab?.focus();
                    }
                  }}
                  className="px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-xs transition cursor-pointer shadow-lg focus:outline-none focus:ring-2 focus:ring-white"
                >
                  Concluir Seleção
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Limite de Perfis Atingido */}
      {showLimitAlert && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-in fade-in">
          <div className="bg-zinc-950 border border-amber-500/40 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center mx-auto shadow-lg">
              <Users className="w-7 h-7" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-black text-white">Limite de Perfis Atingido</h3>
              <p className="text-xs text-zinc-300 leading-relaxed">
                Seu plano contratado permite até <strong className="text-amber-400 font-bold">{maxAllowed} perfil(is)</strong> cadastrados.
              </p>
            </div>
            <div className="p-3.5 rounded-2xl bg-zinc-900/80 border border-zinc-800 text-xs text-zinc-400 text-left space-y-1">
              <p className="font-bold text-white flex items-center gap-1.5">
                💡 O que fazer?
              </p>
              <p>
                Para adicionar um novo perfil, exclua um perfil que não está mais em uso através do botão de gerenciamento.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowLimitAlert(false);
                  setViewState('manage_grid');
                }}
                className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-xs transition cursor-pointer shadow-lg shadow-amber-500/20"
              >
                Gerenciar / Excluir Perfis
              </button>
              <button
                type="button"
                onClick={() => setShowLimitAlert(false)}
                className="px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs transition cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
